import { skipToken } from '@reduxjs/toolkit/query';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useGetPartyGroupByIdQuery, useUpdatePartyGroupMutation } from 'store/api/groups';
import { ClassifierData } from 'types/classifiers';
import { PartyGroup, PartyGroupForm } from 'types/groups';
import { useClassifiers } from 'utils/useClassifiers';
import {
    GROUP_FORM_CLASSIFIERS,
    GROUP_SECTION_FIELDS,
    GroupSectionId,
    groupDtoToForm,
    groupFormToRequest,
} from '../groupFormMapping';
import { GroupClassificationFields, GroupNameField, GroupTypeField, MembersFields, NotesField } from './GroupFields';
import { GroupFormState, useGroupForm } from './useGroupForm';

const SECTION_FIELDS: Record<GroupSectionId, (form: GroupFormState) => ReactNode> = {
    details: (form) => (
        <>
            <GroupNameField form={form} />
            <GroupTypeField form={form} />
            <GroupClassificationFields form={form} />
        </>
    ),
    members: (form) => <MembersFields form={form} />,
    notes: (form) => <NotesField form={form} />,
};

interface SectionFormProps {
    section: GroupSectionId;
    initialData: PartyGroupForm;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function GroupSectionForm({ section, initialData, classifiers, onClose }: SectionFormProps) {
    // The page's own argument, so the save refreshes the entry the page reads: the update does not invalidate it.
    const { id: groupId } = useParams();
    const form = useGroupForm({ type: 'edit', initialData, classifiers });
    const { refetch } = useGetPartyGroupByIdQuery(groupId ?? skipToken);
    const [updateGroup] = useUpdatePartyGroupMutation();

    const onSave = (values: PartyGroupForm) =>
        saveSection(values, {
            section,
            sectionFields: GROUP_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto: PartyGroup) => groupDtoToForm(dto, classifiers),
            toRequest: (edited) => groupFormToRequest(edited),
            update: (request) => updateGroup({ ...request, id: groupId ?? '' }).unwrap(),
            onSaved: onClose,
        });

    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: GroupSectionId;
    partyGroup: PartyGroup;
    onClose: () => void;
}

function GroupSectionEditor({ section, partyGroup, onClose }: EditorProps) {
    const { classifiers, isLoading } = useClassifiers({ codes: GROUP_FORM_CLASSIFIERS });

    const initialData = useFrozenOnceLoaded(!isLoading, () => groupDtoToForm(partyGroup, classifiers));

    if (!initialData) {
        return <Loader />;
    }
    return <GroupSectionForm section={section} initialData={initialData} classifiers={classifiers} onClose={onClose} />;
}

interface GroupSectionProps {
    id: GroupSectionId;
    titleId: string;
    partyGroup: PartyGroup;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the card edits. */
    rows?: Row[];
}

/** A group card on the record page that edits in place with the group form's fields for that card. */
export function GroupSection({ id, titleId, partyGroup, children, rows }: GroupSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <GroupSectionEditor section={id} partyGroup={partyGroup} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
